"use client";

import {
  type ListboxProps,
  type ListboxSectionProps,
  type Selection,
} from "@nextui-org/react";
import React, { useEffect, useMemo } from "react";
import { Listbox, Tooltip, ListboxItem, ListboxSection } from "@nextui-org/react";
import { Icon } from "@iconify/react";

import { cn } from "./cn";
import { useLocation } from "react-router-dom";
import { PressEvent } from "@react-types/shared";

export enum SidebarItemType {
  Nest = "nest",
}

export type SidebarItem = {
  key: string;
  title: string;
  icon?: string;
  iconColor?: string;
  href?: string;
  type?: SidebarItemType.Nest;
  width?: number;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
  show?: boolean;
};

export type SidebarProps = Omit<ListboxProps<SidebarItem>, "children"> & {
  items: SidebarItem[];
  isCompact?: boolean;
  hideEndContent?: boolean;
  iconClassName?: string;
  sectionClasses?: ListboxSectionProps["classNames"];
  classNames?: ListboxProps["classNames"];
  defaultSelectedKey: string;
  onSelect?: (key: string) => void;
  onPress?: (e: PressEvent) => void;
};

const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      isCompact,
      defaultSelectedKey,
      onSelect,
      hideEndContent,
      sectionClasses: sectionClassesProp = {},
      itemClasses: itemClassesProp = {},
      iconClassName,
      classNames,
      className,
      onPress,
      ...props
    },
    ref,
  ) => {
    const pathname = useLocation().pathname;
    const currentLocation = useLocation().pathname.split('/')[1] || 'dashboard';
    const [selected, setSelected] = React.useState<React.Key>(() => currentLocation);
    useEffect(() => {
      if (pathname.includes('/aigent/pdfscanner')) {
        setSelected('pdfscanner');
      } else if (pathname.includes('/aigent/automation')) {
        setSelected('automation');
      } else if (pathname.includes('/analytics')) {
        setSelected('analyst');
      } else {
        setSelected(currentLocation);
      }
    }, [pathname]);
    const sectionClasses = {
      ...sectionClassesProp,
      base: cn(sectionClassesProp?.base, "w-full", {
        "p-0 max-w-[44px]": isCompact,
      }),
      group: cn(sectionClassesProp?.group, {
        "flex flex-col gap-1": isCompact,
      }),
      heading: cn(sectionClassesProp?.heading, "px-3 pb-1.5 pt-[14px] text-xs font-bold uppercase tracking-[0.08em] text-[#a1a1aa]", {
        hidden: isCompact,
      }),
    };

    const itemClasses = {
      ...itemClassesProp,
      base: cn(itemClassesProp?.base, isCompact ? "h-[42px] w-12 gap-0 p-0" : "gap-3 px-3 py-[10px]"),
    };

    const renderedItems = useMemo(() => {
      const visible = items.filter((item) => item.show !== false);
      if (!isCompact) return visible;
      return visible.flatMap((item) => (item.items && item.items.length > 0 ? item.items : [item]));
    }, [isCompact, items]);

    const renderItem = React.useCallback(
      (item: SidebarItem) => {
        if (item?.show === false) return null;

        return (
          <ListboxItem
            id={`sidebar-item-${item?.title}`}
            {...item}
            key={item.key}
            endContent={isCompact || hideEndContent ? null : item.endContent ?? null}
            startContent={
              isCompact ? null : item.icon ? (
                <Icon
                  className={cn(
                    "text-[#71717a] group-data-[selected=true]:text-primary dark:text-default-500",
                    iconClassName
                  )}
                  icon={item.icon}
                  width={item.width ?? 18}
                  style={item.iconColor ? { color: item.iconColor } : undefined}
                />
              ) : (
                item.startContent ?? null
              )
            }
            textValue={item.title}
            title={isCompact ? null : item.title}
            onPress={onPress}
          >
            {isCompact ? (
              <Tooltip content={item.title} placement="right">
                <div className="flex w-full items-center justify-center">
                  {item.icon ? (
                    <Icon
                      className={cn(
                        "text-[#71717a] group-data-[selected=true]:text-primary dark:text-default-500",
                        iconClassName
                      )}
                      icon={item.icon}
                      width={item.width ?? 18}
                      style={item.iconColor ? { color: item.iconColor } : undefined}
                    />
                  ) : (
                    item.startContent ?? null
                  )}
                </div>
              </Tooltip>
            ) : null}
          </ListboxItem>
        );
      },
      [isCompact, hideEndContent, iconClassName, itemClasses?.base],
    );

    return (
      <Listbox
        key={isCompact ? "compact" : "default"}
        ref={ref}
        hideSelectedIcon
        as="nav"
        className={cn("list-none", className)}
        classNames={{
          ...classNames,
          list: cn(isCompact ? "items-center" : "w-full items-stretch", classNames?.list),
        }}
        color="default"
        itemClasses={{
          ...itemClasses,
          base: cn(
            isCompact
              ? "mx-auto my-0.5 h-[42px] w-12 gap-0 rounded-[8px] p-0 data-[hover=true]:bg-[#F4F4F5] data-[selected=true]:bg-[#F4F4F5] dark:data-[hover=true]:bg-content2 dark:data-[selected=true]:bg-content2"
              : "mb-0.5 w-full gap-3 rounded-[8px] px-3 py-[10px] data-[hover=true]:bg-[#F4F4F5] data-[selected=true]:bg-[#F4F4F5] dark:data-[hover=true]:bg-content2 dark:data-[selected=true]:bg-content2",
            itemClasses?.base,
          ),
          title: cn(
            "text-[16px] font-medium leading-5 text-[#181818] group-data-[selected=true]:!font-[600] group-data-[selected=true]:text-primary dark:text-foreground",
            itemClasses?.title,
          ),
        }}
        items={renderedItems}
        selectedKeys={[selected] as unknown as Selection}
        selectionMode="single"
        variant="flat"
        onSelectionChange={(keys) => {
          const key = Array.from(keys)[0];
          setSelected(key as React.Key);
          onSelect?.(key as string);
        }}
        {...props}
        aria-label='Side Navigation'
      >
        {(item) => {
          if (item?.show === false) return null;
          if (item.items && item.items.length > 0 && !isCompact) {
            return (
              <ListboxSection
                key={item.key}
                classNames={sectionClasses}
                title={item.title}
              >
                {item.items.map(renderItem)}
              </ListboxSection>
            );
          }
          return renderItem(item);
        }}
      </Listbox>
    );
  },
);

Sidebar.displayName = "Sidebar";

export default Sidebar;

